import { COUNTRY_CODES } from "@/lib/countries"

// Which domain endings a plan includes, and how to name them.
//
// The allow-list itself is NOT duplicated here: it arrives as the
// `domain_tlds` prop, straight from Openprovider::Registrar::ALLOWED_TLDS,
// so the pricing page can never advertise a TLD the registrar would refuse
// to sell. This module only maps between a country and its ccTLD.
//
// A ccTLD is simply the country's ISO 3166-1 alpha-2 code, lowercased —
// DE -> .de, CH -> .ch. The United Kingdom is the sole exception we sell:
// its ISO code is GB, and it registers a family of endings. The
// entry is an ordered preference — .co.uk is what a UK small business
// actually expects, with bare .uk as the fallback should .co.uk ever
// leave the allow-list.
const TLD_BY_COUNTRY: Record<string, string[]> = { GB: ["co.uk", "uk"] }
const COUNTRY_BY_TLD: Record<string, string> = {
  uk: "GB",
  "co.uk": "GB",
}

// Endings that belong to no single country, so CLDR can't name them. Only
// .eu needs naming of its own — anything else non-country (.com, .org, and
// any .net/.biz added later) is simply available worldwide.
const EUROPE_TLDS = ["eu"]

// Widened to string: COUNTRY_CODES is a literal-union tuple, and the whole
// point here is to test codes that may not be in it.
const ISO_COUNTRIES = new Set<string>(COUNTRY_CODES)

const bare = (tld: string) => tld.replace(/^\./, "").toLowerCase()

// The ISO country a ccTLD belongs to, or null when it isn't a country's at
// all. Never hand a non-country to Intl.DisplayNames: it throws a RangeError
// on a 3-letter code like "NET", which would take the whole pricing page
// down the day someone widens ALLOWED_TLDS.
function isoCountryFor(tld: string): string | null {
  const iso = COUNTRY_BY_TLD[tld] ?? tld.toUpperCase()
  return ISO_COUNTRIES.has(iso) ? iso : null
}

/** The ccTLD we can register for an ISO alpha-2 country (".de"), or null
 *  when the country is unknown or its TLD isn't one we sell. */

/** The market a bare language page speaks to when nothing better is known.
 *  An active geo overlay's country ("de-CH" -> CH) wins, then the visitor's
 *  country from the /geo probe; this is the fallback for the gap between
 *  them — "/de" served before hydration, which is what SSR and any crawler
 *  sees. Display only:
 *  the popover always lists every included ending, and geo overrides this the
 *  moment it resolves. Absent languages (ar spans many markets) fall through
 *  to the country-less endings, which is the honest answer for them. */
const HOME_COUNTRY: Record<string, string> = {
  de: "DE",
  en: "GB",
  es: "ES",
  fr: "FR",
  it: "IT",
  pl: "PL",
  ro: "RO",
  tr: "TR",
  uk: "UA",
  vi: "VN",
}

export function homeCountryForLanguage(
  locale: string | null | undefined,
): string | null {
  const lang = String(locale ?? "")
    .split("-")[0]
    .toLowerCase()
  return HOME_COUNTRY[lang] ?? null
}

export function countryTld(
  country: string | null | undefined,
  allowed: string[],
): string | null {
  if (!country) return null
  const iso = country.toUpperCase()
  // "EU" is not an ISO country and must not resolve off its .eu lookalike.
  // ("CO" is a real one — Colombia — so it resolves only if we ever sell .co.)
  if (!ISO_COUNTRIES.has(iso)) return null

  const candidates = TLD_BY_COUNTRY[iso] ?? [iso.toLowerCase()]
  const tld = candidates.find((c) => allowed.some((a) => bare(a) === c))
  return tld ? `.${tld}` : null
}

/** The TLDs to name inline on a pricing card: the visitor's own country
 *  ending first when we sell it, then .com. Degrades to .com alone — which
 *  is what SSR's first paint renders, before the country is known. */
export function featuredTlds(
  country: string | null | undefined,
  allowed: string[],
): string[] {
  const own = countryTld(country, allowed)
  return own ? [own, ".com"] : [".com"]
}

/** Every allowed TLD with the visitor's featured endings (locally common ccTLD,
 *  then .com) placed first, followed by all other endings in alphabetical order. */
export function orderedTlds(
  country: string | null | undefined,
  allowed: string[],
): string[] {
  const featured = featuredTlds(country, allowed).filter((t) =>
    allowed.includes(t),
  )
  const rest = allowed
    .filter((t) => !featured.includes(t))
    .sort((a, b) => a.localeCompare(b))
  return [...featured, ...rest]
}

/** Every included TLD as a display row — ".de" + "Germany" — for the
 *  popover. The country-less endings lead (anyone can have them, in the
 *  order the registrar lists them), then the countries A-Z by their name in
 *  the reader's own language. A TLD we don't recognize as a country's is
 *  labelled worldwide rather than dropped: the list is a promise about what
 *  a plan includes, so it must stay complete. */
export function tldRows(
  allowed: string[],
  locale: string | null | undefined,
  labels: { worldwide: string; europe: string },
): { tld: string; label: string }[] {
  const loc = locale || "en"
  const display = new Intl.DisplayNames([loc], { type: "region" })

  const generic: { tld: string; label: string }[] = []
  const countries: { tld: string; label: string }[] = []

  for (const entry of allowed) {
    const tld = bare(entry)
    const iso = isoCountryFor(tld)
    if (!iso) {
      generic.push({
        tld: `.${tld}`,
        label: EUROPE_TLDS.includes(tld) ? labels.europe : labels.worldwide,
      })
      continue
    }
    countries.push({ tld: `.${tld}`, label: display.of(iso) ?? iso })
  }

  countries.sort((a, b) => a.label.localeCompare(b.label, loc))
  return [...generic, ...countries]
}
